'use client'

import React, { memo } from 'react'
import { Button } from '../ui/button'
import { SettingSection, settingSections } from '@/types/settings'
import { cn } from '@/lib/utils'

interface SettingsSidebarProps {
  activeSection: string
  onSectionChange: (sectionId: string) => void
  onLoadPreset: (preset: any) => void
}

const SettingsSidebar = memo<SettingsSidebarProps>(({
  activeSection,
  onSectionChange,
  onLoadPreset
}) => {
  return (
    <div className="w-full lg:w-80 lg:border-r border-slate-700/50 p-4 bg-slate-900/30 custom-scrollbar">
      <nav className="space-y-2" role="tablist">
        {settingSections.map((section) => (
          <button
            key={section.id}
            onClick={() => onSectionChange(section.id)}
            className={cn(
              "w-full flex items-center space-x-3 p-4 rounded-lg text-left transition-all duration-200 group relative",
              activeSection === section.id
                ? "bg-gradient-to-r from-cyan-500/20 to-blue-500/20 text-cyan-300 border border-cyan-500/30 shadow-lg shadow-cyan-500/20 scale-105"
                : "hover:bg-slate-800/50 text-slate-300 hover:border-slate-600/50 border border-transparent"
            )}
            role="tab"
          >
            {/* ACTIVE INDICATOR */}
            {activeSection === section.id && (
              <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/5 to-blue-500/5 animate-pulse"></div>
            )}

            {section.icon && (
              <div className={cn(
                "flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center transition-all duration-200",
                activeSection === section.id
                  ? "bg-cyan-500/20 shadow-lg shadow-cyan-500/30 scale-110"
                  : "bg-slate-700/50 group-hover:bg-slate-600/50"
              )}>
                <span className="text-xl">{section.icon}</span>
              </div>
            )}

            <div className="flex-1 min-w-0 relative z-10">
              <div className="font-semibold text-sm">{section.title}</div>
              <div className="text-xs text-slate-400 leading-tight">{section.description}</div>
            </div>

            {/* ACTIVE PULSE */}
            {activeSection === section.id && (
              <div className="w-3 h-3 bg-cyan-400 rounded-full animate-pulse shadow-lg shadow-cyan-400/50"></div>
            )}
          </button>
        ))}
      </nav>

      {/* QUICK PRESETS */}
      <div className="mt-6 p-4 bg-slate-800/30 rounded-lg border border-slate-600/30">
        <h4 className="text-sm font-medium text-slate-200 mb-3">Quick Presets</h4>
        <div className="space-y-2">
          {[
            { name: 'Gaming', desc: 'Ultra Low Latency', preset: { sampleRate: 48000, bufferSize: 64, maxWorkers: 8, quality: 'high' } },
            { name: 'Studio', desc: 'Professional Quality', preset: { sampleRate: 96000, bufferSize: 256, maxWorkers: 4, quality: 'ultra' } },
            { name: 'Performance', desc: 'Maximum Speed', preset: { sampleRate: 44100, bufferSize: 128, maxWorkers: 12, quality: 'medium' } },
            { name: 'Battery', desc: 'Power Saving', preset: { sampleRate: 22050, bufferSize: 512, maxWorkers: 2, quality: 'low' } }
          ].map((preset) => (
            <button
              key={preset.name}
              onClick={() => onLoadPreset(preset.preset)}
              className="w-full text-left p-2 rounded hover:bg-slate-700/50 transition-colors group"
            >
              <div className="text-sm font-medium text-slate-200 group-hover:text-cyan-300">{preset.name}</div>
              <div className="text-xs text-slate-400">{preset.desc}</div>
            </button>
          ))}
        </div>
      </div>
    </div>
  )
})

SettingsSidebar.displayName = 'SettingsSidebar'

export default SettingsSidebar